import React, {useState, useEffect, useRef} from 'react';
import VariablesTable from './VariablesTable';
import UpdatedSamplesTable from './SamplesTable';
import {Box, Typography} from '@mui/material';
import {ButtonRow} from '../../../../components/ButtonRow';
import AddNewSample from '../../../ManageSamples/AddNewSample';
import type {SampleData, SampleErrors} from './types';
import {ReviewContainer} from './ReviewContainer';

/**
 * Returns intersection of samples currently visible in the table and selected samples.
 *
 * @param visibleSamples Array of sample object
 * @param selectedSampleIds Set of selected sample IDs
 */
const getVisibleSelectedSamples = (visibleSamples: SampleData[], selectedSampleIds: Set<string>): SampleData[] => {
    return visibleSamples.filter((sample: SampleData) => selectedSampleIds.has(sample.id));
};

interface RnaReviewProps {
    globalValues: Record<string, any>;
    samples: any[];
    handleValueChange: (newValue: string, key: string, index: number) => void;
    handleVariableChange: (newKey: string, oldKey: string) => void;
    experiment: any;
    reFetchSamples: () => Promise<void>;
    errors: SampleErrors;
    editSample: (sample: any) => Promise<void>;
    setOpenSnackbar: React.Dispatch<React.SetStateAction<boolean>>;
}

/**
 * This monstrosity of prop drilling is to quick and dirty have Step4 be able
 * to show either Rna or Chip. The idea is to eventually (so... good luck
 * reader years in the future) move to this component that which is only
 * relevant here and untangle things. For the time being, it is just ugly. In
 * essence, all the components that are returned here were verbatim in Step4.
 * To keep more overview there, the Rna specific components are moved here. The
 * logic did not follow since that is harder to do so.
 */
export const RnaReview: React.FC<RnaReviewProps> = ({
    globalValues,
    samples,
    handleValueChange,
    handleVariableChange,
    experiment,
    reFetchSamples,
    errors,
    editSample,
    setOpenSnackbar
}) => {
    const [addingSample, setAddingSample] = useState(false);
    const [isDuplicationStarted, setDuplicationStarted] = useState(false);
    const [deleteCallback, setDeleteCallback] = useState<any>(null);
    const [selectedSampleIds, setSelectedSampleIds] = useState<Set<string>>(new Set());
    const [searchTerm, setSearchTerm] = useState('');

    // Derive what samples to display from samples and searchTerm.
    const lowersearch = searchTerm.toLowerCase();
    const visibleSamples = !searchTerm ? samples : samples?.filter((sample: any) => sample?.name?.toLowerCase().includes(lowersearch));

    const visibleSelectedSamples = getVisibleSelectedSamples(visibleSamples || [], selectedSampleIds || new Set());

    /**
     * Keep track of the timeout used to debounce the search input.
     */
    const debounceSearchTimeoutRef = useRef<NodeJS.Timeout | null>(null);
    /**
     * Sets the search term to the value of the input field. Uses debouncing to
     * not fire off too many consecutive updates.
     */
    const handleSearchChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        const value = event.target.value;
        if (debounceSearchTimeoutRef.current) {
            clearTimeout(debounceSearchTimeoutRef.current);
        }
        debounceSearchTimeoutRef.current = setTimeout(() => {
            setSearchTerm(value);
            debounceSearchTimeoutRef.current = null;
        }, 300);
    };

    useEffect(
        () => () => {
            if (debounceSearchTimeoutRef.current) clearTimeout(debounceSearchTimeoutRef.current);
        },
        []
    );

    /**
     * Adds or removes a sample from the selected samples.
     *
     * Uses setSelectedSamplesIds.
     */
    const handleSelectRow = (id: any) => {
        const newSelectedSamples = new Set(selectedSampleIds);

        if (id && selectedSampleIds.has(id)) {
            newSelectedSamples.delete(id);
        } else {
            newSelectedSamples.add(id);
        }

        setSelectedSampleIds(newSelectedSamples);
    };

    /**
     * Removes a sample from the selected samples.
     *
     * Uses setSelectedSamplesIds.
     */
    const handleRemoveSample = (sampleId: string | undefined) => {
        if (typeof sampleId === 'string') {
            setSelectedSampleIds((prevSelectedSamples: Iterable<string> | ArrayLike<string>) => {
                const updatedSelectedSamples = new Set(Array.from(prevSelectedSamples));
                updatedSelectedSamples.delete(sampleId);

                return updatedSelectedSamples;
            });
        }
    };

    useEffect(() => {
        if (!visibleSelectedSamples.length) {
            setDuplicationStarted(false);
        }
    }, [visibleSelectedSamples]);

    return (
        <>
            <ReviewContainer>
                <VariablesTable
                    globalValues={globalValues}
                    data={visibleSamples}
                    handleValueChange={handleValueChange}
                    handleVariableChange={handleVariableChange}
                />
            </ReviewContainer>
            <ReviewContainer>
                <Typography variant="headline" size="small">
                    Review your individual samples
                </Typography>
                <Typography mb={3} variant="body" size="medium">
                    Make changes to the values assigned to your individual samples, and review your sample pairings to make sure they are
                    accurate.
                </Typography>
                {addingSample && (
                    <Box py={1} sx={{borderBottom: '1px solid', borderColor: 'outline-variant'}}>
                        <AddNewSample
                            data={visibleSamples}
                            exp={experiment}
                            setAdding={setAddingSample}
                            sampleSource={visibleSamples[0] || [{}]}
                            reFetchSamples={reFetchSamples}
                        />
                    </Box>
                )}

                {isDuplicationStarted &&
                    visibleSelectedSamples.map(sample => (
                        <AddNewSample
                            key={sample.id}
                            data={visibleSamples}
                            exp={experiment}
                            setAdding={setAddingSample}
                            isDuplicationStarted={isDuplicationStarted}
                            sampleSource={sample}
                            onRemoveSample={handleRemoveSample}
                            reFetchSamples={reFetchSamples}
                        />
                    ))}

                {!addingSample && !isDuplicationStarted && (
                    <ButtonRow
                        onSearchChange={handleSearchChange}
                        addClick={setAddingSample}
                        onDelete={deleteCallback}
                        areSelectedSamples={selectedSampleIds.size > 0}
                        setDuplicationStarted={setDuplicationStarted}
                    />
                )}

                <UpdatedSamplesTable
                    experiment={experiment}
                    samples={visibleSamples}
                    errors={errors}
                    globalValues={globalValues}
                    fetchSamples={reFetchSamples}
                    setDeleteCallback={setDeleteCallback}
                    editSample={(sample: any) => {
                        editSample(sample);
                        reFetchSamples();
                    }}
                    setSuccess={setOpenSnackbar}
                    handleSelectRow={handleSelectRow}
                    selectedSamples={selectedSampleIds}
                    setSelectedSamples={setSelectedSampleIds}
                />
            </ReviewContainer>
        </>
    );
};
